/**
 * Word API Utility Functions
 * TypeScript utilities for interacting with Microsoft Word via Office.js
 */

// Word's body.search() has two hard limits that make naive anchor matching
// flaky on real contracts:
//   1. The search string cannot cross a paragraph mark.
//   2. The search string cannot exceed 255 characters.
// On top of that, contracts are full of "smart" quotes/dashes and irregular
// whitespace, so an anchor copied from the AI (or from extracted plaintext)
// rarely matches the live document byte-for-byte. We therefore search on a
// single-paragraph, <=255-char fragment using Word's punctuation/space-
// insensitive options, then disambiguate to ONE target range so we never
// clobber other clauses. See applyRedlineWithTrackChanges below.

const WORD_SEARCH_MAX = 255;

/**
 * Break an anchor into searchable fragments: each fragment is within a single
 * paragraph and <= 255 chars (Word.search limits). Ordered longest-first so we
 * try the most distinctive fragment before falling back to shorter ones.
 */
function toSearchFragments(anchor: string): string[] {
  const lines = anchor
    .split(/[\r\n\u000B\u000C\u2028\u2029]+/)
    .map((l) => l.trim())
    .filter((l) => l.length >= 4);
  const frags: string[] = [];
  for (const line of lines) {
    if (line.length <= WORD_SEARCH_MAX) {
      frags.push(line);
      continue;
    }
    // Split an over-long line into <=255-char chunks at word boundaries.
    let rest = line;
    while (rest.length > WORD_SEARCH_MAX) {
      let cut = rest.lastIndexOf(' ', WORD_SEARCH_MAX);
      if (cut < 40) cut = WORD_SEARCH_MAX;
      frags.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest.length >= 4) frags.push(rest);
  }
  return frags.sort((a, b) => b.length - a.length);
}

/**
 * Prepare a fragment for Word.search: strip control chars and collapse
 * whitespace. We deliberately do NOT escape brackets/parentheses — with
 * matchWildcards off they are literal, and escaping them would make
 * placeholder text like "[RENT AMOUNT]" impossible to find. Punctuation
 * differences are handled by the ignorePunct search option instead.
 */
function sanitizeForSearch(q: string): string {
  return q.replace(/[\u0000-\u001F]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Word's body.search does strict character matching, but LLM-emitted anchors and
 * the live docx routinely disagree on smart vs straight quotes, non-breaking
 * spaces, soft hyphens, en/em dashes and ellipses — any one mismatch makes the
 * search return zero hits and the redline silently fails. This yields a small
 * set of equivalent query strings to try in order; the first with matches wins.
 */
export function generateSearchVariants(query: string): string[] {
  const variants = new Set<string>();
  variants.add(query);

  const ascii = query
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/ /g, ' ')
    .replace(/­/g, '')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...');
  if (ascii !== query) variants.add(ascii);

  const smart = query
    .replace(/'/g, '’')
    .replace(/"/g, '”')
    .replace(/\.\.\./g, '…');
  if (smart !== query) variants.add(smart);

  // PAIRED smart quotes — the common real case Word produces: a straight
  // ("term") becomes an opening then a closing curly quote alternately, so it
  // matches the doc's balanced curly quotes around a defined term.
  let dq = 0;
  let sq = 0;
  const paired = query
    .replace(/"/g, () => (dq++ % 2 === 0 ? '“' : '”'))
    .replace(/'/g, () => (sq++ % 2 === 0 ? '‘' : '’'))
    .replace(/\.\.\./g, '…');
  if (paired !== query && paired !== smart) variants.add(paired);

  return Array.from(variants)
    .map((v) => (v.length > WORD_SEARCH_MAX ? v.substring(0, WORD_SEARCH_MAX) : v))
    .filter((v) => v.trim().length > 0);
}

// ---- Word-level diff engine (for minimal-diff redlines) --------------------
// Track-change ONLY the words that changed, instead of striking the whole
// anchor — so "add a sentence to a clause" doesn't restrike the clause.
// Word-level LCS diff: diffWordOps / coalesceOpsIntoSpans.

/** One changed region between two unchanged runs. old/new may be '' (pure ins/del). */
export interface DiffSpan {
  oldText: string;
  newText: string;
  leftContext: string;
  rightContext: string;
}

/** Fold the char variants Word/AI disagree on — for token EQUALITY only. */
function normalizeToken(token: string): string {
  return token
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/ /g, ' ')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...');
}

interface DiffOp {
  t: 'eq' | 'del' | 'ins';
  w: string;
}

/** LCS word alignment → op stream. Quote/dash/ws-insensitive equality. */
function diffWordOps(original: string, suggested: string): DiffOp[] {
  const a = original.split(' ').filter(Boolean);
  const b = suggested.split(' ').filter(Boolean);
  const na = a.map(normalizeToken);
  const nb = b.map(normalizeToken);
  const eq = (i: number, j: number) => na[i] === nb[j];
  const dp: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array(b.length + 1).fill(0)
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i][j] = eq(i, j) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (eq(i, j)) {
      ops.push({ t: 'eq', w: a[i++] });
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ t: 'del', w: a[i++] });
    } else {
      ops.push({ t: 'ins', w: b[j++] });
    }
  }
  while (i < a.length) ops.push({ t: 'del', w: a[i++] });
  while (j < b.length) ops.push({ t: 'ins', w: b[j++] });
  return ops;
}

/** Coalesce runs of non-equal ops into spans, capturing 1 unchanged word each side. */
export function computeWordDiffSpans(original: string, suggested: string): DiffSpan[] {
  const ops = diffWordOps(original, suggested);
  const spans: DiffSpan[] = [];
  let cur: { oldWords: string[]; newWords: string[]; leftContext: string } | null = null;
  let lastEq = '';
  const close = (rightContext: string) => {
    if (!cur) return;
    spans.push({
      oldText: cur.oldWords.join(' '),
      newText: cur.newWords.join(' '),
      leftContext: cur.leftContext,
      rightContext,
    });
    cur = null;
  };
  for (const op of ops) {
    if (op.t === 'eq') {
      close(op.w);
      lastEq = op.w;
    } else {
      if (!cur) cur = { oldWords: [], newWords: [], leftContext: lastEq };
      if (op.t === 'del') cur.oldWords.push(op.w);
      else cur.newWords.push(op.w);
    }
  }
  close('');
  return spans;
}

/** Result of an apply attempt. `ambiguous` = anchor matched >1 place; no edit made.
 *  `locations` = a short context snippet for each place the edit was applied
 *  (document order), so the chat can list them with per-location Jump links. */
export type ApplyResult = {
  success: boolean;
  matchCount: number;
  ambiguous?: boolean;
  error?: string;
  locations?: string[];
};

/**
 * Locate the anchor in the document, tolerant to quote/dash/space drift and the
 * 255-char / single-paragraph search limits. Prefers a fragment that yields a
 * UNIQUE match. When the only matches are non-unique (the anchor repeats),
 * returns ambiguous=true and NO range — the caller then asks the user which
 * occurrence, instead of guessing and editing the wrong clause.
 */
async function locateAnchorRange(
  context: Word.RequestContext,
  anchorText: string
): Promise<{ range: Word.Range | null; ambiguous: boolean }> {
  const doc = context.document;
  const fragments = toSearchFragments(anchorText);
  if (fragments.length === 0) return { range: null, ambiguous: false };

  const searchOnce = async (q: string, tolerant: boolean): Promise<Word.RangeCollection | null> => {
    const safe = sanitizeForSearch(q);
    if (!safe) return null;
    try {
      const opts = tolerant
        ? { matchCase: false, matchWholeWord: false, ignorePunct: true, ignoreSpace: true }
        : { matchCase: false, matchWholeWord: false };
      const r = doc.body.search(safe, opts as Word.SearchOptions);
      r.load('items');
      await context.sync();
      return r;
    } catch {
      return null;
    }
  };

  // Search a fragment: char-variants (strict) first (handles smart/straight
  // quote & dash drift), then a punctuation/space-tolerant pass, then
  // progressively shortened from the end. Returns the first collection with hits.
  const searchFragment = async (fragment: string): Promise<Word.RangeCollection | null> => {
    const attempts = [fragment];
    let q = fragment;
    while (q.length > 24) {
      const sp = q.lastIndexOf(' ');
      if (sp < 24) break;
      q = q.slice(0, sp).replace(/[\s.,;:!?")\]}']+$/, '').trim();
      if (q) attempts.push(q);
    }
    for (const a of attempts) {
      for (const variant of generateSearchVariants(a)) {
        const r = await searchOnce(variant, false);
        if (r && r.items.length > 0) return r;
      }
      const t = await searchOnce(a, true);
      if (t && t.items.length > 0) return t;
    }
    return null;
  };

  let ambiguousCandidate: Word.RangeCollection | null = null;
  for (const frag of fragments) {
    const r = await searchFragment(frag);
    if (r && r.items.length === 1) return { range: r.items[0], ambiguous: false };
    if (r && r.items.length > 1 && !ambiguousCandidate) ambiguousCandidate = r;
  }
  if (ambiguousCandidate) return { range: null, ambiguous: true };
  return { range: null, ambiguous: false };
}

/**
 * Locate EVERY occurrence of `anchorText`. Uses the most distinctive fragment
 * that yields hits and returns all of its matches (document order). Backs the
 * "replace it everywhere it appears" behaviour — when an anchor recurs (e.g. a
 * defined term used in several clauses) we edit them all rather than refusing.
 */
async function locateAllAnchorRanges(
  context: Word.RequestContext,
  anchorText: string
): Promise<Word.Range[]> {
  const doc = context.document;
  const fragments = toSearchFragments(anchorText);
  const runSearch = async (q: string, tolerant: boolean): Promise<Word.Range[]> => {
    const safe = sanitizeForSearch(q);
    if (!safe) return [];
    try {
      const opts = tolerant
        ? { matchCase: false, matchWholeWord: false, ignorePunct: true, ignoreSpace: true }
        : { matchCase: false, matchWholeWord: false };
      const r = doc.body.search(safe, opts as Word.SearchOptions);
      r.load('items');
      await context.sync();
      return r.items;
    } catch {
      return [];
    }
  };
  for (const frag of fragments) {
    for (const variant of generateSearchVariants(frag)) {
      const hits = await runSearch(variant, false);
      if (hits.length > 0) return hits;
    }
    const tolerant = await runSearch(frag, true);
    if (tolerant.length > 0) return tolerant;
  }
  return [];
}

const MAX_MINIMAL_SPANS = 8;

/**
 * Minimise a redline over `range`: word-diff its current text vs `replacement`
 * and track-change ONLY the changed regions (replace changed words; insert added
 * words next to an unchanged neighbour) — so surrounding text isn't restruck.
 * Returns false when the change can't be placed safely (caller full-replaces).
 * The feasibility pass runs BEFORE any mutation, so a false return never
 * half-applies.
 */
async function applyMinimalDiff(
  context: Word.RequestContext,
  range: Word.Range,
  replacementText: string
): Promise<boolean> {
  range.load('text');
  await context.sync();
  const original = (range.text || '').replace(/\s+/g, ' ').trim();
  const replacement = replacementText.replace(/\s+/g, ' ').trim();
  const spans = computeWordDiffSpans(original, replacement);
  if (spans.length === 0) return true;
  if (spans.length > MAX_MINIMAL_SPANS) return false;

  const replacements = new Map<string, { newText: string; count: number }>();
  const insertions: Array<{ newText: string; anchor: string; after: boolean }> = [];
  for (const s of spans) {
    if (
      s.oldText.length > WORD_SEARCH_MAX ||
      s.leftContext.length > WORD_SEARCH_MAX ||
      s.rightContext.length > WORD_SEARCH_MAX
    ) {
      return false;
    }
    if (s.oldText && s.newText) {
      const existing = replacements.get(s.oldText);
      if (existing && existing.newText !== s.newText) return false;
      if (existing) existing.count++;
      else replacements.set(s.oldText, { newText: s.newText, count: 1 });
    } else if (!s.oldText && s.newText && (s.leftContext || s.rightContext)) {
      insertions.push({
        newText: s.newText,
        anchor: s.leftContext || s.rightContext,
        after: !!s.leftContext,
      });
    } else {
      return false; // pure deletion / anchorless insertion → full replace is safer
    }
  }

  // Feasibility pass — no mutation. Every replacement's match count must equal
  // how many times the diff changes it; a surplus means some occurrences are
  // UNCHANGED and a replace-all would corrupt them → bail to full replace.
  const replSearches = [...replacements.entries()].map(([oldText, info]) => ({
    info,
    search: range.search(oldText, { matchCase: true, matchWholeWord: false }),
  }));
  const insSearches = insertions.map((ins) => ({
    ins,
    search: range.search(ins.anchor, { matchCase: true, matchWholeWord: false }),
  }));
  [...replSearches, ...insSearches].forEach((t) => t.search.load('items'));
  await context.sync();
  if (!replSearches.every((t) => t.search.items.length === t.info.count)) return false;
  if (!insSearches.every((t) => t.search.items.length === 1)) return false;

  // Apply. Replacements right-to-left so a tracked insertion can't shift an
  // earlier-resolved range; then insertions.
  const replTargets: Array<{ found: Word.Range; newText: string }> = [];
  for (const t of replSearches) {
    for (const item of t.search.items) replTargets.push({ found: item, newText: t.info.newText });
  }
  for (let k = replTargets.length - 1; k >= 0; k--) {
    replTargets[k].found.insertText(replTargets[k].newText, Word.InsertLocation.replace);
  }
  for (let k = insSearches.length - 1; k >= 0; k--) {
    const { ins, search } = insSearches[k];
    const found = search.items[0];
    found.insertText(
      ins.after ? ' ' + ins.newText : ins.newText + ' ',
      ins.after ? Word.InsertLocation.after : Word.InsertLocation.before
    );
  }
  await context.sync();
  return true;
}

/** Turn Track Changes ON, run `work`, then restore the user's prior setting. */
async function withTrackChanges(
  context: Word.RequestContext,
  work: () => Promise<ApplyResult>
): Promise<ApplyResult> {
  const doc = context.document;
  doc.load('changeTrackingMode');
  await context.sync();
  const previousMode = doc.changeTrackingMode;
  const restore = async () => {
    try {
      doc.changeTrackingMode = previousMode;
      await context.sync();
    } catch {}
  };
  try {
    doc.changeTrackingMode = (Word as any).ChangeTrackingMode?.trackAll || 'TrackAll';
    await context.sync();
    const out = await work();
    await restore();
    return out;
  } catch (err: any) {
    await restore();
    return { success: false, matchCount: 0, error: err?.message || 'Apply failed' };
  }
}

/**
 * Apply a redline as a tracked change. `redlineMode`:
 *   - 'surgical'  (default): edit only the located anchor range, minimally
 *     (word-diff → strike only the changed words).
 *   - 'paragraph': rewrite the whole enclosing paragraph, minimally.
 * Returns ambiguous=true (and makes NO edit) when the anchor isn't unique, so
 * the chat can ask which occurrence rather than editing the wrong clause.
 */
export async function applyRedlineWithTrackChanges(
  anchorText: string,
  replacementText: string,
  redlineMode: 'surgical' | 'paragraph' = 'surgical'
): Promise<ApplyResult> {
  if (!isWordApiAvailable()) {
    return { success: false, matchCount: 0, error: 'Word API not available' };
  }
  if (!anchorText || anchorText.trim().length === 0) {
    return { success: false, matchCount: 0, error: 'anchorText is empty' };
  }
  if (typeof replacementText !== 'string') {
    return { success: false, matchCount: 0, error: 'replacement is not text' };
  }

  return new Promise((resolve) => {
    Word.run(async (context: Word.RequestContext) => {
      const out = await withTrackChanges(context, async () => {
        // Replace the anchor EVERYWHERE it appears (not just a unique match).
        const ranges = await locateAllAnchorRanges(context, anchorText);
        if (ranges.length === 0) {
          return { success: false, matchCount: 0, error: 'Anchor text not found in document' };
        }
        // Pass 1: capture a location label (enclosing paragraph snippet) per
        // occurrence BEFORE editing, in document order — for the chat's list.
        const paras = ranges.map((r) => r.paragraphs.getFirst());
        paras.forEach((p) => p.load('text'));
        await context.sync();
        const locations = paras.map((p) => {
          const s = (p.text || '').replace(/\s+/g, ' ').trim();
          return s.length > 90 ? s.slice(0, 90) + '…' : s;
        });
        // Pass 2: apply last → first so a tracked edit never shifts an earlier,
        // not-yet-edited occurrence's position.
        let applied = 0;
        for (let i = ranges.length - 1; i >= 0; i--) {
          const range = ranges[i];
          const target =
            redlineMode === 'paragraph' ? range.paragraphs.getFirst().getRange() : range;
          const minimal = await applyMinimalDiff(context, target, replacementText);
          if (!minimal) {
            target.insertText(replacementText, Word.InsertLocation.replace);
            await context.sync();
          }
          applied++;
        }
        return { success: applied > 0, matchCount: applied, locations };
      });
      resolve(out);
    }).catch((err: Error) => {
      resolve({ success: false, matchCount: 0, error: err.message });
    });
  });
}

/**
 * Insert new text (e.g. a whole new clause/section) as a tracked change,
 * positioned relative to an existing anchor paragraph — WITHOUT rewriting the
 * anchor. This is the robust path for "add a new Section": the AI supplies a
 * short unique anchor (a nearby heading/sentence) plus the new clause text, and
 * we insert it as new paragraph(s) after/before that paragraph.
 */
export async function insertClauseRelativeToAnchor(
  anchorText: string,
  newText: string,
  position: 'after' | 'before' = 'after'
): Promise<ApplyResult> {
  if (!isWordApiAvailable()) {
    return { success: false, matchCount: 0, error: 'Word API not available' };
  }
  if (!anchorText || anchorText.trim().length === 0) {
    return { success: false, matchCount: 0, error: 'anchorText is empty' };
  }
  if (typeof newText !== 'string' || newText.trim().length === 0) {
    return { success: false, matchCount: 0, error: 'insert text is empty' };
  }

  const paras = newText.split(/\r?\n/).map((p) => p.trimEnd()).filter((p) => p.trim().length > 0);
  if (paras.length === 0) {
    return { success: false, matchCount: 0, error: 'insert text is empty' };
  }

  return new Promise((resolve) => {
    Word.run(async (context: Word.RequestContext) => {
      const out = await withTrackChanges(context, async () => {
        const { range, ambiguous } = await locateAnchorRange(context, anchorText);
        if (ambiguous) {
          return {
            success: false,
            matchCount: 0,
            ambiguous: true,
            error: 'Anchor matches multiple locations',
          };
        }
        if (!range) {
          return { success: false, matchCount: 0, error: 'Anchor text not found in document' };
        }
        const anchorPara = range.paragraphs.getFirst();
        if (position === 'after') {
          // Insert sequentially so the new paragraphs keep their order.
          let ref: Word.Paragraph = anchorPara;
          for (const line of paras) {
            ref = ref.insertParagraph(line, Word.InsertLocation.after);
          }
        } else {
          // Insert each new paragraph immediately before the anchor, in order:
          // each insertion lands directly ahead of the anchor, so forward order
          // yields para0, para1, …, anchor.
          for (const line of paras) {
            anchorPara.insertParagraph(line, Word.InsertLocation.before);
          }
        }
        await context.sync();
        return { success: true, matchCount: 1 };
      });
      resolve(out);
    }).catch((err: Error) => {
      resolve({ success: false, matchCount: 0, error: err.message });
    });
  });
}

/**
 * Remove a DUPLICATED clause/section/sentence: locate every occurrence of
 * `anchorText`, keep the FIRST, and delete the rest as tracked changes. When a
 * duplicate's paragraph is preceded by the same heading as the first occurrence
 * (e.g. a repeated "1. Purpose" heading + body), the heading is deleted too so
 * no orphan heading is left behind. Ambiguity is EXPECTED here (that's what a
 * duplicate is), so — unlike applyRedlineWithTrackChanges — multiple matches is
 * the success case, not a bail-out. Returns matchCount = copies removed.
 */
export async function removeDuplicateClause(anchorText: string): Promise<ApplyResult> {
  if (!isWordApiAvailable()) {
    return { success: false, matchCount: 0, error: 'Word API not available' };
  }
  if (!anchorText || anchorText.trim().length === 0) {
    return { success: false, matchCount: 0, error: 'anchorText is empty' };
  }

  return new Promise((resolve) => {
    Word.run(async (context: Word.RequestContext) => {
      const out = await withTrackChanges(context, async () => {
        const doc = context.document;
        const fragments = toSearchFragments(anchorText);

        // Find the search that surfaces the MOST occurrences (the duplicated
        // block). Prefer a fragment/variant returning ≥2 hits; remember a single
        // hit as a fallback so we can report "only one copy exists".
        let best: Word.RangeCollection | null = null;
        for (const frag of fragments) {
          for (const variant of generateSearchVariants(frag)) {
            const safe = sanitizeForSearch(variant);
            if (!safe) continue;
            let r: Word.RangeCollection;
            try {
              r = doc.body.search(safe, { matchCase: false, matchWholeWord: false } as Word.SearchOptions);
            } catch {
              continue;
            }
            r.load('items');
            await context.sync();
            if (r.items.length >= 2) {
              best = r;
              break;
            }
            if (r.items.length === 1 && !best) best = r;
          }
          if (best && best.items.length >= 2) break;
        }

        if (!best) {
          return { success: false, matchCount: 0, error: 'Clause text not found in document' };
        }
        if (best.items.length < 2) {
          return {
            success: false,
            matchCount: 1,
            error: 'Only one copy of that clause exists — nothing to de-duplicate',
          };
        }

        // Keep item[0]; delete item[1..]. Resolve each duplicate's paragraph,
        // its preceding heading, and its following paragraph up front.
        const firstBody = best.items[0].paragraphs.getFirst();
        const firstPrev = firstBody.getPreviousOrNullObject();
        firstPrev.load('text');
        const dupBodies = best.items.slice(1).map((rng) => rng.paragraphs.getFirst());
        const dupPrevs = dupBodies.map((p) => p.getPreviousOrNullObject());
        const dupNexts = dupBodies.map((p) => p.getNextOrNullObject());
        dupPrevs.forEach((p) => p.load('text'));
        await context.sync();

        const norm = (s: string): string => (s || '').replace(/\s+/g, ' ').trim();
        const headingText = firstPrev.isNullObject ? null : norm(firstPrev.text);

        // Delete right-to-left so earlier ranges aren't shifted by earlier edits.
        for (let i = dupBodies.length - 1; i >= 0; i--) {
          const body = dupBodies[i];
          const prev = dupPrevs[i];
          const next = dupNexts[i];
          const includeHeading =
            headingText !== null && !prev.isNullObject && norm(prev.text) === headingText;
          const startPara = includeHeading ? prev : body;
          const startRange = startPara.getRange('Start');
          const endRange = next.isNullObject ? body.getRange('End') : next.getRange('Start');
          const del = startRange.expandTo(endRange);
          del.insertText('', Word.InsertLocation.replace);
        }
        await context.sync();
        return { success: true, matchCount: dupBodies.length };
      });
      resolve(out);
    }).catch((err: Error) => {
      resolve({ success: false, matchCount: 0, error: err.message });
    });
  });
}


/**
 * Scroll Word to (and select) the given text — used by the chat's "Jump to
 * change" button. Searches quote/dash/space-tolerant, single-paragraph
 * fragments; selects the first match (Word scrolls on a selection change, so we
 * collapse-then-reselect to force the scroll). Best-effort; returns whether it
 * landed on something.
 */
export async function goToTextInDocument(text: string, occurrenceIndex = 0): Promise<boolean> {
  if (!isWordApiAvailable() || !text || text.trim().length === 0) return false;
  return new Promise((resolve) => {
    Word.run(async (context: Word.RequestContext) => {
      const body = context.document.body;
      const fragments = toSearchFragments(text);
      const tries = fragments.length ? fragments : [text.slice(0, WORD_SEARCH_MAX)];
      let target: Word.Range | null = null;
      for (const frag of tries) {
        for (const variant of generateSearchVariants(sanitizeForSearch(frag))) {
          try {
            const r = body.search(variant, { matchCase: false, matchWholeWord: false });
            r.load('items');
            await context.sync();
            if (r.items.length > 0) {
              // Jump to the requested occurrence (clamped) so a per-location
              // link in a multi-place edit lands on its own spot.
              target = r.items[Math.min(occurrenceIndex, r.items.length - 1)];
              break;
            }
          } catch {
            /* try next variant */
          }
        }
        if (target) break;
      }
      if (target) {
        // Scroll the viewport to the match. Word only scrolls on a selection
        // CHANGE, so first move the caret elsewhere (document start) then select
        // the target — this forces a scroll even if it was already selected.
        try {
          context.document.body.getRange('Start').select();
          await context.sync();
        } catch {
          /* ignore — best effort */
        }
        target.select();
        await context.sync();
        // A second identical select is a no-op for scroll; instead re-issue the
        // selection after a tick so Word commits the viewport move.
        target.select();
        await context.sync();
        resolve(true);
      } else {
        resolve(false);
      }
    }).catch(() => resolve(false));
  });
}

/**
 * Inserts text at the current cursor position in the Word document
 * @param text - The text to insert
 * @returns Promise that resolves when the text is inserted
 */
export async function insertTextAtCursor(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        // Get the current selection (cursor position)
        const selection = context.document.getSelection();
        
        // Insert the text at the cursor position
        selection.insertText(text, Word.InsertLocation.replace);
        
        // Sync the context to execute the batch operation
        await context.sync();
        
        resolve();
      }).catch((error: Error) => {
        console.error('Error inserting text:', error);
        reject(error);
      });
    } catch (error) {
      console.error('Error in insertTextAtCursor:', error);
      reject(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
}

export async function getSelectedText(): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        const sel = context.document.getSelection();
        sel.load('text');
        await context.sync();
        resolve(sel.text || '');
      }).catch((error: Error) => {
        console.error('Error getting selected text:', error);
        reject(error);
      });
    } catch (error) {
      console.error('Error in getSelectedText:', error);
      reject(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
}

export async function replaceSelectedText(replaceText: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        const sel = context.document.getSelection();
        sel.insertText(replaceText, Word.InsertLocation.replace);
        await context.sync();
        resolve();
      }).catch((error: Error) => {
        console.error('Error replacing selected text:', error);
        reject(error);
      });
    } catch (error) {
      console.error('Error in replaceSelectedText:', error);
      reject(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
}

/**
 * Inserts text at the end of the document
 * @param text - The text to insert
 * @returns Promise that resolves when the text is inserted
 */
export async function insertTextAtEnd(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        // Get the body of the document
        const body = context.document.body;
        
        // Insert text at the end
        body.insertText(text, Word.InsertLocation.end);
        
        // Sync the context to execute the batch operation
        await context.sync();
        
        resolve();
      }).catch((error: Error) => {
        console.error('Error inserting text at end:', error);
        reject(error);
      });
    } catch (error) {
      console.error('Error in insertTextAtEnd:', error);
      reject(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
}

/**
 * Inserts formatted text (with paragraph break) at the cursor position
 * @param text - The text to insert
 * @returns Promise that resolves when the text is inserted
 */
export async function insertFormattedClause(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        // Get the current selection
        const selection = context.document.getSelection();
        
        // Insert text with a paragraph break before and after it
        selection.insertText(`\n${text}\n`, Word.InsertLocation.replace);
        
        // Sync the context to execute the batch operation
        await context.sync();
        
        resolve();
      }).catch((error: Error) => {
        console.error('Error inserting formatted clause:', error);
        reject(error);
      });
    } catch (error) {
      console.error('Error in insertFormattedClause:', error);
      reject(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
}

/**
 * Gets the text content of the entire document
 * @returns Promise that resolves with the document text
 */
export async function getDocumentText(): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        // Plain body text — cheap and reliable. (We deliberately do NOT serialize
        // the whole document via getOoxml on every message: on large contracts it
        // is heavy, can return empty, and disturbs Word Online's rendering.)
        const body = context.document.body;
        body.load('text');
        await context.sync();
        resolve(body.text);
      }).catch((error: Error) => {
        console.error('Error getting document text:', error);
        reject(error);
      });
    } catch (error) {
      console.error('Error in getDocumentText:', error);
      reject(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
}

/**
 * Replaces a specific text in the document with new text.
 * When both searchText and replaceText are present, first tries to find the
 * combined string (searchText + replaceText) and replace it with replaceText only.
 * This "accept suggestion" behavior avoids duplicating the new text when the
 * document already shows tracked change as "original" + "revised" (e.g. "into new").
 * @param searchText - The text to search for and replace (or original part of change)
 * @param replaceText - The text to replace it with (or revised part of change)
 * @returns Promise that resolves when the replacement is complete
 */
export async function replaceTextInDocument(searchText: string, replaceText: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        const body = context.document.body;
        const opts = { matchCase: false, matchWholeWord: false };

        // Accept-suggestion mode: document may show "original" + "revised" (e.g. "into new").
        // Replace the entire span with just "revised" so we get "new", not "new new".
        if (searchText && replaceText) {
          const combined = searchText + replaceText;
          const searchCombined = body.search(combined, opts);
          searchCombined.load('items');
          await context.sync();
          if (searchCombined.items.length > 0) {
            for (let i = 0; i < searchCombined.items.length; i++) {
              searchCombined.items[i].insertText(replaceText, Word.InsertLocation.replace);
            }
            await context.sync();
            resolve();
            return;
          }
          // If combined not found, try with space between (e.g. "into " + "new" in doc)
          const combinedWithSpace = searchText + ' ' + replaceText;
          const searchWithSpace = body.search(combinedWithSpace, opts);
          searchWithSpace.load('items');
          await context.sync();
          if (searchWithSpace.items.length > 0) {
            for (let i = 0; i < searchWithSpace.items.length; i++) {
              searchWithSpace.items[i].insertText(replaceText, Word.InsertLocation.replace);
            }
            await context.sync();
            resolve();
            return;
          }
        }

        // Fallback: replace only the original text
        const searchResults = body.search(searchText, opts);
        searchResults.load('items');
        await context.sync();
        for (let i = 0; i < searchResults.items.length; i++) {
          searchResults.items[i].insertText(replaceText, Word.InsertLocation.replace);
        }
        await context.sync();
        resolve();
      }).catch((error: Error) => {
        console.error('Error replacing text:', error);
        reject(error);
      });
    } catch (error) {
      console.error('Error in replaceTextInDocument:', error);
      reject(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
}

/**
 * Highlights text in the document by searching for it and applying highlight
 * @param searchText - The text to search for and highlight
 * @returns Promise that resolves when the text is highlighted
 */
export async function highlightTextInDocument(searchText: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        // Get the body of the document
        const body = context.document.body;
        
        // Search for the text
        const searchResults = body.search(searchText, { matchCase: false, matchWholeWord: false });
        searchResults.load('items');
        
        await context.sync();
        
        // Highlight each occurrence and select the first one
        for (let i = 0; i < searchResults.items.length; i++) {
          const range = searchResults.items[i];
          // Use font.highlightColor for highlighting
          range.font.highlightColor = '#FFFF00'; // Yellow highlight
          if (i === 0) {
            range.select(); // Select the first occurrence to scroll to it
          }
        }
        
        await context.sync();
        
        resolve();
      }).catch((error: Error) => {
        console.error('Error highlighting text:', error);
        reject(error);
      });
    } catch (error) {
      console.error('Error in highlightTextInDocument:', error);
      reject(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
}

/**
 * Checks if Word API is available
 * @returns boolean indicating if Word API is available
 */
export function isWordApiAvailable(): boolean {
  return typeof Word !== 'undefined' && Word !== null && typeof Word.run === 'function';
}

/**
 * Read Ezylex contract context from the document's **custom document
 * properties** (docProps/custom.xml), which the WOPI GetFile endpoint stamps
 * onto every Ezylex-served document (see EzyReact lib/injectDocProps.ts).
 *
 * This is the authoritative "am I attached to an Ezylex contract?" signal:
 * custom properties are readable via Word.run regardless of the document URL
 * (unavailable in Word-on-the-web WOPI) or the add-in's manifest id (unlike
 * webextension settings). A foreign OneDrive/SharePoint document has none, so
 * this returns null and the add-in shows its welcome screen.
 */
export async function readContractContextFromDocProps(): Promise<{
  contractId: string;
  tenantId: string;
} | null> {
  // On a COLD first load the taskpane can mount before Office/Word has finished
  // initializing (so Word.run isn't ready yet) AND before Word has populated the
  // document's custom properties. A single early read therefore returns nothing
  // and the add-in wrongly shows the welcome screen — which is exactly why it
  // "worked only after a manual reload" (the reload hits a warm Office).
  //
  // So poll: on each attempt wait for the Word API to be available, then read
  // the properties, retrying over a generous window until they appear. A genuine
  // contract resolves as soon as the properties load (early return); a foreign
  // document simply never has them and we give up after the window.
  const MAX_ATTEMPTS = 25; // ~17s of polling to cover slow cold loads
  const INTERVAL_MS = 700;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (isWordApiAvailable()) {
      try {
        const result = await Word.run(async (context) => {
          const props = context.document.properties.customProperties;
          props.load('items/key,items/value');
          await context.sync();
          const items = props.items || [];
          const get = (k: string): string => {
            const match = items.find((p) => p.key === k);
            return match ? String(match.value ?? '') : '';
          };
          const contractId = get('ezylex_contract_id');
          const tenantId = get('ezylex_tenant_id');
          if (contractId && tenantId) return { contractId, tenantId };
          return null;
        });
        if (result) return result;
      } catch (err) {
        console.warn(`[wordApi] readContractContextFromDocProps attempt ${attempt} failed:`, err);
      }
    }
    if (attempt < MAX_ATTEMPTS) await new Promise((r) => setTimeout(r, INTERVAL_MS));
  }
  return null;
}

export async function getOfficeDocumentUrl(): Promise<string | null> {
  // Try multiple sources because Office Online + WOPI documents sometimes
  // don't return a URL from getFilePropertiesAsync (it returns null), but
  // the WOPI src is usually accessible via document.referrer (Office Online
  // is our parent frame and the WOPISrc is in its URL).
  try {
    if (typeof Office === 'undefined' || !Office?.context?.document) {
      // Fall through to referrer-based detection below.
    } else {
      // 1) Some Office versions expose .url as a synchronous property.
      const syncUrl = (Office.context.document as any).url;
      if (typeof syncUrl === 'string' && syncUrl) return syncUrl;

      // 2) Async file properties — works for SharePoint-hosted docs, often
      // returns null for WOPI/Office Online.
      if (Office.context.document.getFilePropertiesAsync) {
        const asyncUrl = await new Promise<string | null>((resolve) => {
          try {
            Office.context.document.getFilePropertiesAsync((r: any) => {
              if (r?.status === Office.AsyncResultStatus.Succeeded) {
                resolve(r?.value?.url || null);
              } else {
                resolve(null);
              }
            });
          } catch {
            resolve(null);
          }
        });
        if (asyncUrl) return asyncUrl;
      }
    }
  } catch (error) {
    console.warn('getOfficeDocumentUrl Office API error:', error);
  }

  // 3) Last resort — parse the WOPISrc out of document.referrer.
  // The addin iframe is loaded by Office Online; its URL contains
  // ?WOPISrc=https%3A%2F%2Flc.drupallers.com%2Fwopi%2Ffiles%2F<fileId>...
  try {
    const ref = (typeof document !== 'undefined' && document.referrer) || '';
    if (ref) {
      const m = ref.match(/[?&]WOPISrc=([^&]+)/i);
      if (m && m[1]) {
        const decoded = decodeURIComponent(m[1]);
        console.log('[wordApi] Recovered doc URL from referrer WOPISrc');
        return decoded;
      }
    }
  } catch (error) {
    console.warn('getOfficeDocumentUrl referrer parse error:', error);
  }

  return null;
}

/**
 * Gets all comments from the Word document
 * @returns Promise that resolves with an array of comments
 */
export async function getDocumentComments(): Promise<Array<{
  id: string;
  author: string;
  content: string;
  date: string;
  rangeText: string;
  rangeStart: number;
  rangeEnd: number;
  resolved?: boolean;
}>> {
  return new Promise((resolve, reject) => {
    if (!isWordApiAvailable()) {
      reject(new Error('Word API is not available'));
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        const body = context.document.body;
        const comments = body.getComments();
        comments.load('items');
        await context.sync();

        // Store ranges in a map to access later
        const rangeMap = new Map<string, Word.Range>();
        
        for (const c of comments.items) {
          c.load('id, authorName, content, creationDate, resolved');
          const r = c.getRange();
          r.load('text');
          rangeMap.set(c.id, r);
        }
        await context.sync();

        const commentsData = comments.items.map((c) => {
          const r = rangeMap.get(c.id);
          let rangeText = '';
          try {
            rangeText = r?.text || '';
          } catch {
            rangeText = '';
          }
          return {
            id: c.id,
            author: c.authorName || 'Unknown',
            content: c.content || '',
            date: c.creationDate ? c.creationDate.toISOString() : new Date().toISOString(),
            rangeText,
            rangeStart: 0,
            rangeEnd: 0,
            resolved: !!c.resolved,
          };
        });

        resolve(commentsData);
      }).catch((error: Error) => {
        console.error('[getDocumentComments] Fatal error:', error);
        // Return empty array instead of rejecting to not break the analysis
        resolve([]);
      });
    } catch (error) {
      console.error('Error in getDocumentComments:', error);
      // Return empty array instead of rejecting
      resolve([]);
    }
  });
}

/**
 * Scrolls to and selects text in the document
 * @param text - The text to find and scroll to
 * @returns Promise that resolves when the text is found and selected
 */
export async function scrollToText(text: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (!isWordApiAvailable()) {
      resolve(false);
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        const body = context.document.body;
        // Search for the text (use first 100 chars to avoid issues with long text)
        const searchText = text.substring(0, 100).trim();
        if (!searchText) {
          resolve(false);
          return;
        }
        
        const searchResults = body.search(searchText, { matchCase: false, matchWholeWord: false });
        searchResults.load('items');
        await context.sync();

        if (searchResults.items.length > 0) {
          // Select and scroll to the first match
          searchResults.items[0].select();
          await context.sync();
          resolve(true);
        } else {
          resolve(false);
        }
      }).catch((error: Error) => {
        console.error('[scrollToText] Error:', error);
        resolve(false);
      });
    } catch (error) {
      console.error('Error in scrollToText:', error);
      resolve(false);
    }
  });
}

/**
 * Gets tracked changes (revisions) from the Word document
 * Note: This requires Word API 1.6+ and may not be available in all versions
 * @returns Promise that resolves with an array of tracked changes
 */
export async function getTrackedChanges(): Promise<Array<{
  id: string;
  type: string;
  author: string;
  date: string;
  originalText: string;
  revisedText: string;
}>> {
  return new Promise((resolve) => {
    if (!isWordApiAvailable()) {
      resolve([]);
      return;
    }

    try {
      Word.run(async (context: Word.RequestContext) => {
        // Try to get tracked changes using the revisions API
        // Note: This API may not be available in all Word versions
        const body = context.document.body;
        
        // Load the body's text to look for strikethrough and underline formatting
        // which typically indicate tracked changes
        body.load('text');
        const ooxmlResult = body.getOoxml();
        await context.sync();

        const ooxml = ooxmlResult.value || '';

        const getAttrByLocalName = (el: Element, localName: string): string => {
          for (const attr of Array.from(el.attributes)) {
            if (attr.localName === localName) return attr.value;
          }
          return '';
        };

        const findAncestorByLocalName = (el: Element, localName: string): Element | null => {
          let current: Element | null = el;
          while (current) {
            if (current.localName === localName) return current;
            current = current.parentElement;
          }
          return null;
        };

        const extractRevisionText = (el: Element): string => {
          const delTextNodes = Array.from(el.getElementsByTagNameNS('*', 'delText'));
          const textNodes = delTextNodes.length > 0
            ? delTextNodes
            : Array.from(el.getElementsByTagNameNS('*', 't'));
          return textNodes.map((n) => n.textContent || '').join('');
        };

        let xmlDoc: Document | null = null;
        try {
          xmlDoc = new DOMParser().parseFromString(ooxml, 'application/xml');
        } catch {
          resolve([]);
          return;
        }

        if (!xmlDoc || xmlDoc.getElementsByTagName('parsererror').length > 0) {
          resolve([]);
          return;
        }

        const revisionElements = Array.from(xmlDoc.getElementsByTagName('*'))
          .filter((el) => el.localName === 'ins' || el.localName === 'del');

        const revisions = revisionElements
          .map((el, index) => {
            const type = el.localName;
            const rawId = getAttrByLocalName(el, 'id');
            const author = getAttrByLocalName(el, 'author');
            const date = getAttrByLocalName(el, 'date');
            const text = extractRevisionText(el);
            const paragraph = findAncestorByLocalName(el, 'p');
            const id = rawId ? `${type}-${rawId}` : `${type}-idx-${index}`;
            return { id, type, author, date, text, paragraph };
          })
          .filter((rev) => (rev.text || '').trim().length > 0);

        const canPair = (a: (typeof revisions)[number], b: (typeof revisions)[number]): boolean => {
          if (!a || !b) return false;
          if (!(a.type === 'del' && b.type === 'ins') && !(a.type === 'ins' && b.type === 'del')) return false;
          if (a.paragraph && b.paragraph && a.paragraph !== b.paragraph) return false;
          if (a.author && b.author && a.author !== b.author) return false;
          if (a.date && b.date && a.date !== b.date) return false;
          return true;
        };

        const results: Array<{
          id: string;
          type: string;
          author: string;
          date: string;
          originalText: string;
          revisedText: string;
        }> = [];

        for (let i = 0; i < revisions.length; i++) {
          const current = revisions[i];
          const next = revisions[i + 1];

          if (next && canPair(current, next)) {
            const del = current.type === 'del' ? current : next;
            const ins = current.type === 'ins' ? current : next;
            results.push({
              id: `replace-${del.id}-${ins.id}`,
              type: 'replace',
              author: del.author || ins.author || '',
              date: del.date || ins.date || '',
              originalText: del.text || '',
              revisedText: ins.text || '',
            });
            i++;
            continue;
          }

          if (current.type === 'ins') {
            results.push({
              id: `ins-${current.id}`,
              type: 'insertion',
              author: current.author || '',
              date: current.date || '',
              originalText: '',
              revisedText: current.text || '',
            });
            continue;
          }

          if (current.type === 'del') {
            results.push({
              id: `del-${current.id}`,
              type: 'deletion',
              author: current.author || '',
              date: current.date || '',
              originalText: current.text || '',
              revisedText: '',
            });
            continue;
          }
        }

        resolve(results);
      }).catch((error: Error) => {
        console.error('[getTrackedChanges] Error:', error);
        resolve([]);
      });
    } catch (error) {
      console.error('Error in getTrackedChanges:', error);
      resolve([]);
    }
  });
}

